<template>
  <div id="app-container" ref="appContainer">
    <div ref="chatBubble" id="chat-bubble" :class="chatWindowOpen ? 'chat-bubble-hide-for-mobile' : ''">
      <ChatBubble></ChatBubble>
    </div>
    <div id="chat-starters" ref="chatStarters" v-if="!chatWindowOpen && startersVisible">
      <ChatStarters></ChatStarters>
    </div>
    <div ref="chatWindow" id="chat-window-container" v-show="chatWindowOpen">
      <ChatWindow></ChatWindow>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import ChatBubble from "./components/ChatBubble.vue";
import ChatWindow from "./components/ChatWindow.vue";
import ChatStarters from "./components/ChatStarters.vue";
import { useMessagesStore } from "./stores/useMessagesStore";
import { useLeadStore } from "./stores/useLeadStore";
import { useWindowStateStore } from "./stores/useWindowStateStore";

const appContainer = ref(null);
const chatBubble = ref(null);
const chatStarters = ref(null);
const chatWindow = ref(null);

const messagesStore = useMessagesStore();
const { initialize, dispose } = messagesStore;

const leadStore = useLeadStore();
const { initialize: initializeLead, dispose: disposeLead } = leadStore;

const windowStateStore = useWindowStateStore();
const { chatWindowOpen, startersVisible } = storeToRefs(windowStateStore);
const { toggleChatWindow, initializeStarters } = windowStateStore;

const stopWatching = watch(chatWindowOpen, (newVal) => {
  if (newVal) {
    listenForOutsideClicks();
  } else {
    document.removeEventListener("click", handleOutsideClick);
  }
});

// Close the chat window if user clicks anywhere outside the window
function listenForOutsideClicks() {
  document.addEventListener("click", handleOutsideClick);
}

/**
 * Close the chat window if user clicks anywhere outside the window
 * @param {*} event
 */
const handleOutsideClick = (event) => {
  if (chatBubble.value.contains(event.target)) return;
  else if (chatStarters.value) {
    if (chatStarters.value.contains(event.target)) return;
  }
  if (!chatWindow.value) return;
  const x = event.clientX;
  const y = event.clientY;
  const windowRect = chatWindow.value.getBoundingClientRect();

  if (
    x < windowRect.left ||
    x > windowRect.right ||
    ((y < windowRect.top || y > windowRect.bottom) && chatWindowOpen.value)
  ) {
    toggleChatWindow();
  }
};

onMounted(() => {
  // Initialize the messages store
  initialize();
  initializeLead();
  initializeStarters();
  
  adjustHeight();
  window.addEventListener('resize', adjustHeight);
  window.addEventListener('orientationchange', adjustHeight);
  window.addEventListener('focus', adjustHeight);
});

onUnmounted(() => {
  // Clean up messages store listeners
  dispose();
  disposeLead();
  
  window.removeEventListener('resize', adjustHeight);
  window.removeEventListener('orientationchange', adjustHeight);
  document.removeEventListener("click", handleOutsideClick);
  stopWatching();
});

const adjustHeight = () => {
  if (appContainer.value) {
    appContainer.value.style.height = `${window.innerHeight}px`;
  }
};

// function clearChat() {
//   messagesStore.clearChat();
// }

</script>

<style>
body {
  margin: 0;
}

#app {
  display: flex;
  justify-content: flex-end;
}

/* The container for the entire app */
#app-container {
  height: 100vh;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column-reverse;
  flex-wrap: nowrap;
  align-items: flex-end;
  z-index: 1;
  overflow: auto;
}

@media (min-width: 768px) {
  #app-container {
    padding-left: 20px;
    padding-top: 20px;
    padding-bottom: 20px;
    padding-right: 20px;
    max-width: 600px; /* TODO: Random width for now, determine late */
  }

  #chat-window-container {
    max-height: 80vh;
    margin-bottom: 20px;
  }

  #chat-bubble {
    padding-bottom: 0px;
    padding-right: 0px;
  }

  #chat-starters {
    padding-right: 0px;
  }
}

/* The container for the chat window */
#chat-window-container {
  flex-grow: 1;
  width: 100%;
  
  box-sizing: border-box;
  height: 100%;
}

.number-badge {
  position: relative;
  top: 12px;
  left: calc(100% - 24px);
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0px 0px 12px 0px #e6a643;
  background: #e88e00;

  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: 0.02em;
}

@media screen and (max-width: 768px) {
  .chat-bubble-hide-for-mobile {
    display: none !important;
  }

  #chat-bubble {
    padding-bottom: 20px;
    padding-right: 20px;
  }

  #chat-starters {
    padding-right: 20px;
  }
}
</style>
